import { useCallback, useRef, useState } from 'react'
import {
  desfazer as desfazerHistorico,
  historicoVazio,
  podeDesfazer as temPassado,
  podeRefazer as temFuturo,
  refazer as refazerHistorico,
  registrar as registrarHistorico,
  type Historico,
} from '../dominio/historico'

/**
 * Janela em que duas mudanças da MESMA coisa contam como um passo só.
 *
 * Sem isso, arrastar o slider de rotação de um calço criaria um passo de
 * desfazer por pixel arrastado, e desfazer viraria inútil. É o mesmo problema
 * que o Table top resolve coalescendo o arrasto do gizmo.
 */
export const JANELA_COALESCENCIA_MS = 700

export interface ControleHistorico {
  /**
   * Chame ANTES de alterar o estado: guarda o instante anterior.
   *
   * `chave` identifica o que está sendo alterado. Duas chamadas com a mesma
   * chave dentro de `JANELA_COALESCENCIA_MS` viram um passo só.
   */
  registrar: (chave?: string) => void
  desfazer: () => void
  refazer: () => void
  podeDesfazer: boolean
  podeRefazer: boolean
  /** Esquece o histórico. Usado ao trocar de cenário dentro do módulo. */
  limpar: () => void
}

/**
 * Desfazer/refazer para um módulo, por instantâneo.
 *
 * O `Historico<T>` do domínio já era genérico e testado, mas só o Table top o
 * usava — nos outros módulos, remover um calço ou quebrar um vidro era
 * irreversível a não ser pelo botão de restaurar tudo. Este hook liga os dois
 * lados sem mexer no formato do estado de cada módulo: `capturar` lê o que
 * importa, `restaurar` escreve de volta, e nada mais precisa mudar.
 *
 * O instantâneo deve conter SÓ o que a ação altera. Guardar estado demais faz
 * desfazer reverter coisas que o usuário não pediu — voltar a posição da câmera
 * ou o módulo aberto, por exemplo.
 */
export function useHistoricoDeModulo<T>(
  capturar: () => T,
  restaurar: (valor: T) => void,
): ControleHistorico {
  const [historico, setHistorico] = useState<Historico<T>>(historicoVazio<T>)
  const ultimoRegistro = useRef<{ chave: string; instante: number } | null>(null)

  // `capturar` e `restaurar` fecham sobre o estado e mudam de identidade a cada
  // render. Guardados em ref, os callbacks abaixo ficam estáveis e não
  // reconstroem a árvore de ferramentas a cada tecla digitada num painel.
  const capturarRef = useRef(capturar)
  const restaurarRef = useRef(restaurar)
  const historicoRef = useRef(historico)
  capturarRef.current = capturar
  restaurarRef.current = restaurar
  historicoRef.current = historico

  const registrar = useCallback((chave?: string) => {
    const agora = Date.now()
    const ultimo = ultimoRegistro.current
    if (chave != null && ultimo?.chave === chave && agora - ultimo.instante < JANELA_COALESCENCIA_MS) {
      ultimoRegistro.current = { chave, instante: agora }
      return
    }
    ultimoRegistro.current = chave == null ? null : { chave, instante: agora }
    const anterior = capturarRef.current()
    setHistorico((atual) => registrarHistorico(atual, anterior))
  }, [])

  /* O efeito colateral fica FORA do atualizador do useState: em modo estrito o
     React chama o atualizador duas vezes, e restaurar lá dentro aplicaria o
     instantâneo em duplicata. */
  const desfazer = useCallback(() => {
    const resultado = desfazerHistorico(historicoRef.current, capturarRef.current())
    if (!resultado) return
    restaurarRef.current(resultado.estado)
    setHistorico(resultado.historico)
    ultimoRegistro.current = null
  }, [])

  const refazer = useCallback(() => {
    const resultado = refazerHistorico(historicoRef.current, capturarRef.current())
    if (!resultado) return
    restaurarRef.current(resultado.estado)
    setHistorico(resultado.historico)
    ultimoRegistro.current = null
  }, [])

  const limpar = useCallback(() => {
    ultimoRegistro.current = null
    setHistorico(historicoVazio<T>())
  }, [])

  return {
    registrar,
    desfazer,
    refazer,
    podeDesfazer: temPassado(historico),
    podeRefazer: temFuturo(historico),
    limpar,
  }
}
